import { request } from "../../api";
import { unwrapList } from "../devices";

export type URLPoolKind = "domain" | "ipv4" | "ipv6" | "cidr";
export type URLVulnStatus =
  | "unknown"
  | "pending"
  | "clean"
  | "suspicious"
  | "malicious";
export type URLVulnSeverity = "" | "low" | "medium" | "high";
export type CompanyURLDecision = "inherit" | "allow" | "block";

export interface URLPoolRow {
  id: string;
  kind: URLPoolKind;
  value: string;
  first_seen_at: string;
  last_seen_at: string;
  seen_count: number;
  vuln_status: URLVulnStatus;
  vuln_severity: URLVulnSeverity;
  vuln_checked_at: string | null;
  vuln_source: string;
  vuln_note: string;
  company_decision: CompanyURLDecision | null;
  company_note: string | null;
  rule_usage_count: number;
}

export interface URLPoolFilters {
  kind?: URLPoolKind;
  vuln_status?: URLVulnStatus;
  decision?: CompanyURLDecision;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ScanNowResult {
  ok: boolean;
  url_pool_id?: string;
  from?: URLVulnStatus;
  to?: URLVulnStatus;
  reason?: string;
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export interface URLPoolPage {
  count: number;
  results: URLPoolRow[];
  next: string | null;
  previous: string | null;
}

export const urlPoolApi = {
  list: async (filters: URLPoolFilters = {}) => {
    const data = await request<Paginated<URLPoolRow> | URLPoolRow[]>(
      `/api/admin/v1/url-pool/${qs(filters)}`,
    );
    return unwrapList(data);
  },

  listPage: async (filters: URLPoolFilters = {}): Promise<URLPoolPage> => {
    const data = await request<Paginated<URLPoolRow> | URLPoolRow[]>(
      `/api/admin/v1/url-pool/${qs(filters)}`,
    );
    if (Array.isArray(data)) {
      return {
        count: data.length,
        results: data,
        next: null,
        previous: null,
      };
    }
    return {
      count: data.count ?? data.results?.length ?? 0,
      results: data.results ?? [],
      next: data.next ?? null,
      previous: data.previous ?? null,
    };
  },

  create: (input: { kind: URLPoolKind; value: string }) =>
    request<URLPoolRow>(`/api/admin/v1/url-pool/`, {
      method: "POST",
      json: input,
    }),

  update: (
    id: string,
    patch: Partial<{
      vuln_status: URLVulnStatus;
      vuln_severity: URLVulnSeverity;
      vuln_note: string;
    }>,
  ) =>
    request<URLPoolRow>(`/api/admin/v1/url-pool/${id}/`, {
      method: "PATCH",
      json: patch,
    }),

  scanNow: (id: string) =>
    request<ScanNowResult>(`/api/admin/v1/url-pool/${id}/scan-now/`, {
      method: "POST",
      json: {},
    }),

  setDecision: (
    id: string,
    body: { decision: CompanyURLDecision; note?: string },
  ) =>
    request<URLPoolRow>(`/api/admin/v1/url-pool/${id}/decision/`, {
      method: "PATCH",
      json: body,
    }),
};
